import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEOHead from "../components/SEOHead";
import { useLanguage } from "../contexts/LanguageContext";

/**
 * Produktübersicht der BROVKO GmbH.
 * Die Produkte treten als Beleg für die KI-Value-Engine auf, nicht als
 * Portfolio-Galerie: Wir beraten KI-Transformation nicht nur, wir bauen sie.
 */

const seo = {
  title: "Produkte & Umsetzungskompetenz | Was wir selbst bauen | BROVKO",
  description:
    "Sechs eigene Produkte der BROVKO GmbH, fünf davon im Markt, vier im App Store. Native iOS-Apps, Python-Backends, KI-Anbindung und Betrieb in deutschen Rechenzentren.",
  keywords:
    "BROVKO Produkte, Granalis, Vox2Doc, Domicus, Fleetdoc, Fairspark, Replyly, KI-Produkte, Eigenentwicklung",
  canonicalUrl: "/produkte",
};

const kennzahlen = [
  { k: "Eigene Produkte", v: "6", d: "Vom Assistenten bis zur Datenplattform" },
  { k: "Davon im Markt", v: "5", d: "Live, im App Store oder bei Kunden" },
  { k: "Apps im App Store", v: "4", d: "Durch Apples Prüfung gegangen" },
  { k: "Betrieb", v: "DE", d: "Deutsche Rechenzentren, DSGVO-konform" },
];

/* Die Kette von der Idee in den Betrieb. Jeder Schritt ist an eigenen
   Produkten durchlaufen worden, nicht aus einem Methodenhandbuch. */
const kette = [
  { n: "01", t: "Problem schärfen",
    p: "Wir starten nicht bei der Technologie, sondern bei dem, was ohne KI teuer bleibt. Jedes unserer Produkte ist aus einem konkreten Engpass entstanden." },
  { n: "02", t: "Schnitt festlegen",
    p: "Was gehört in die erste Fassung, was ausdrücklich nicht. Ein zu großer Schnitt ist der häufigste Grund, warum KI-Projekte nie in den Betrieb kommen." },
  { n: "03", t: "Bauen",
    p: "Native App, Weboberfläche, Backend, Datenmodell, KI-Anbindung. Aus einer Hand, ohne Übergabeverluste zwischen Gewerken." },
  { n: "04", t: "In den Markt bringen",
    p: "App-Store-Prüfung, Datenschutzerklärung, Preismodell, Onboarding. Der Teil, den Prototypen nie erreichen." },
  { n: "05", t: "Betreiben und nachschärfen",
    p: "Überwachung, Fehlerbehebung im laufenden Betrieb, Migrationen, neue Versionen. Hier entscheidet sich, ob ein Produkt trägt." },
];

/* Faehigkeiten mit konkretem Stack. Alle Techniken stammen aus den
   Projektunterlagen der sechs Produkte, nichts ist hinzuerfunden. */
const koennen = [
  { t: "Native iOS-Apps",
    p: "Vier Apps sind durch Apples Prüfung gegangen und stehen im App Store. Wir kennen den Ablauf inklusive der Stellen, an denen Einreichungen abgelehnt werden.",
    stack: ["Swift 6", "SwiftUI", "SwiftData", "Apple Sign-In"] },
  { t: "Weboberflächen",
    p: "Von der Landingpage bis zur vollwertigen Oberfläche mit Rollen, Rechten und Mandantentrennung.",
    stack: ["React 19", "Next.js 16", "TypeScript", "Tailwind CSS"] },
  { t: "Backends und Datenmodelle",
    p: "Asynchrone Python-Backends mit versionierten Datenbankmigrationen. Kein Datenmodell, das beim ersten Schema-Wechsel zerbricht.",
    stack: ["FastAPI", "SQLAlchemy async", "Alembic", "PostgreSQL 16"] },
  { t: "KI-Anbindung",
    p: "Sprache zu Text mit Sprechererkennung, Bilderkennung, Textextraktion, Zusammenfassung. Immer mit der Frage, was passiert, wenn das Modell danebenliegt.",
    stack: ["Claude API", "Claude Vision", "Deepgram", "OCR"] },
  { t: "Beweissicherheit",
    p: "Wo Dokumentation standhalten muss, reicht ein PDF nicht. Qualifizierte Zeitstempel und Integritäts-Hashes gehören ins Fundament, nicht obendrauf.",
    stack: ["RFC 3161", "Integritäts-Hash", "Audit-Trail"] },
  { t: "Betrieb",
    p: "Wir übergeben nicht an einen Dienstleister, sondern betreiben selbst. Deutsche Rechenzentren, eigene Auslieferung, eigene Überwachung.",
    stack: ["Docker Compose", "Caddy 2", "Hetzner Cloud", "Let's Encrypt"] },
];

/* Was aus dem eigenen Betrieb gelernt wurde. Bewusst konkret statt allgemein. */
const gelernt = [
  { t: "Der Betrieb bringt die Fehler, die kein Test findet",
    p: "Ein Datenbank-Lock, der auf einer verwaisten Verbindung hängen bleibt. Eine Namenszuordnung, die an einem Umlaut scheitert. Solche Fehler entstehen erst unter echten Bedingungen, und man findet sie nur, wenn man selbst betreibt." },
  { t: "Die letzten zehn Prozent sind die Hälfte der Arbeit",
    p: "Zwischen einem funktionierenden Prototyp und einer App, die durch Apples Prüfung geht, liegen Datenschutzerklärung, Abrechnung, Kontolöschung und Sonderfälle. Wer das nicht eingeplant hat, kommt nicht in den Markt." },
  { t: "Ohne Rückweg fängt niemand an",
    p: "Vollexport, Datenmitnahme, keine Bindung an ein einzelnes Modell oder eine einzelne Cloud. Diese Zusage entscheidet häufig darüber, ob ein Vorhaben überhaupt startet." },
];

const produkte = [
  {
    name: "Vox2Doc",
    domain: "vox2doc.de",
    href: "https://vox2doc.de",
    bild: "/img/produkte/vox2doc-hero.jpg",
    text: "Der Meeting-Assistent nimmt das Gespräch auf, transkribiert es mit Sprechererkennung und liefert danach Protokoll, Zusammenfassung und Aufgabenliste. Funktioniert mit Google Meet, Zoom und Teams, indem er als Teilnehmer eingeladen wird.",
    stand: "Im App Store",
    detail: "iOS und Web, Hosting in Deutschland",
  },
  {
    name: "Domicus",
    domain: "domicus.de",
    href: "https://domicus.de",
    bild: "/img/produkte/domicus-hero.jpg",
    text: "Die Plattform für das Immobilienportfolio: Objekte verwalten, eine digitale Objektakte führen und Übergaben beweissicher dokumentieren. Mit KI-gestützter Zählerstanderkennung und Integritäts-Hash auf jedem Protokoll.",
    stand: "Im App Store",
    detail: "iOS und Web, für Mieter kostenlos",
  },
  {
    name: "Fleetdoc",
    domain: "fleetdoc.de",
    href: "https://fleetdoc.de",
    bild: "/img/produkte/fleetdoc-hero.jpg",
    text: "Fahrzeugübergaben und Schäden strukturiert dokumentieren, für Autovermietungen, Logistik und Fuhrparks. Die App führt Schritt für Schritt, die KI beschreibt den Schaden auf dem Foto, ein qualifizierter Zeitstempel macht das Protokoll belastbar.",
    stand: "B2B-Plattform",
    detail: "in Pilotierung",
  },
  {
    name: "Fairspark",
    domain: "fairspark.de",
    href: "https://fairspark.de",
    bild: "/img/produkte/fairspark-hero.jpg",
    text: "Aus dem Foto einer beschädigten Lieferung wird ein fertiges Mängelprotokoll. Die KI erkennt den Schaden und beschreibt ihn, das PDF geht direkt an den Händler. Auspacken, prüfen, fertig.",
    stand: "Im App Store",
    detail: "iOS",
  },
  {
    name: "Replyly",
    domain: "replyly.online",
    href: "https://replyly.online",
    bild: "/img/produkte/replyly-hero.jpg",
    text: "Wer per Text kommuniziert, wird nicht gesehen, sondern gelesen. Replyly liefert drei Antwortvorschläge in vier Tonalitäten, damit man so klingt, wie man wirken will. Die Auswahl bleibt beim Nutzer.",
    stand: "Im App Store",
    detail: "iOS, Endkundenprodukt",
  },
];

const Produkte = () => {
  useLanguage();

  return (
    <div className="theme-brand min-h-screen bg-surface text-ink">
      <SEOHead {...seo} />
      <Header />

      {/* Auftakt */}
      <section className="relative overflow-hidden pt-32 pb-20">
        <div className="absolute inset-0 bg-brand-hero" aria-hidden="true" />
        <div className="relative container mx-auto px-4">
          <span className="inline-flex items-center rounded-full border border-brand-400/25 bg-brand/10 px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-brand-300">
            Aus eigener Entwicklung
          </span>
          <h1 className="mt-6 max-w-[17ch] text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
            Wir beraten KI-Transformation nicht nur.{" "}
            <span className="text-brand-400">Wir bauen sie.</span>
          </h1>
          <p className="mt-6 max-w-[62ch] text-base leading-relaxed text-ink-muted sm:text-lg">
            Sechs eigene Produkte aus dem Haus BROVKO. Fünf davon sind im Markt, vier haben Apples
            Prüfung bestanden. Sie sind der belastbarste Beleg dafür, dass unsere
            Methodik trägt: Wir wenden sie zuerst auf uns selbst an.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-x-7 gap-y-4">
            <a
              href="#koennen"
              className="inline-flex h-11 items-center gap-2 rounded-lg bg-brand px-6 text-sm font-bold text-ink-onBrand shadow-[0_10px_30px_-12px_rgba(20,184,166,.8)] transition-colors duration-200 hover:bg-brand-600"
            >
              Was wir umsetzen können
              <ArrowRight className="h-4 w-4" />
            </a>
            <a
              href="#portfolio"
              className="inline-flex items-center gap-2 text-sm font-semibold text-brand-300 transition-colors hover:text-brand-400"
            >
              Alle Produkte
              <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Kennzahlen */}
      <section className="border-y border-hairline bg-surface-sunken">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
            {kennzahlen.map((m, i) => (
              <div
                key={m.k}
                className={`px-6 py-10 lg:px-8 ${i < 3 ? "lg:border-r lg:border-hairline" : ""} ${
                  i < 2 ? "sm:border-r sm:border-hairline" : ""
                } border-b border-hairline sm:border-b-0`}
              >
                <div className="text-[11px] uppercase tracking-[0.16em] text-ink-faint">{m.k}</div>
                <div className="mt-3.5 text-3xl font-extrabold tracking-tight lg:text-4xl">{m.v}</div>
                <div className="mt-2 text-sm text-ink-muted">{m.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Das verbindende Muster */}
      <section className="py-24">
        <div className="container mx-auto max-w-[64ch] px-4">
          <h2 className="text-2xl font-extrabold tracking-tight sm:text-3xl">
            Ein Muster zieht sich durch alles, was wir bauen
          </h2>
          <p className="mt-5 text-base leading-relaxed text-ink-muted">
            Vier unserer sechs Produkte lösen dieselbe Aufgabe in unterschiedlichen Welten: Sie
            überführen einen unstrukturierten Zustand in einen belegten Nachweis. Ein
            Dokumentenberg, ein Gespräch, eine Fahrzeugübergabe, eine beschädigte Lieferung. Am
            Ende steht jedes Mal etwas, das nachvollziehbar ist und einer Prüfung standhält.
          </p>
          <p className="mt-4 text-base leading-relaxed text-ink-muted">
            Genau das ist der Kern der KI-Value-Engine. Wir behaupten nicht, dass KI wirkt. Wir
            bauen Systeme, die es belegen können.
          </p>
        </div>
      </section>

      {/* ---- Umsetzungskompetenz: die Kette von der Idee in den Betrieb ---- */}
      <section id="umsetzung" className="border-y border-hairline bg-surface-sunken py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-[64ch]">
            <span className="inline-flex items-center rounded-full border border-brand-400/25 bg-brand/10 px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-brand-300">
              Umsetzungskompetenz
            </span>
            <h2 className="mt-5 text-2xl font-extrabold tracking-tight sm:text-3xl">
              Von der Idee bis in den Betrieb
            </h2>
            <p className="mt-5 text-base leading-relaxed text-ink-muted">
              Die meisten KI-Vorhaben scheitern nicht am Modell, sondern an der Strecke danach. Wir
              haben diese Strecke sechsmal selbst zurückgelegt, mit eigenem Geld und eigenem Risiko.
            </p>
          </div>

          <ol className="mt-12 border-t border-hairline">
            {kette.map((s) => (
              <li
                key={s.n}
                className="grid gap-2 border-b border-hairline py-6 transition-[background-color,padding] duration-300 ease-brand hover:bg-brand/[0.045] hover:pl-4 md:grid-cols-[70px_260px_1fr] md:gap-8"
              >
                <span className="text-sm font-bold tabular-nums text-brand-300">{s.n}</span>
                <h3 className="text-base font-bold tracking-tight">{s.t}</h3>
                <p className="max-w-[62ch] text-sm leading-relaxed text-ink-muted">{s.p}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ---- Was wir selbst bauen ---- */}
      <section id="koennen" className="py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-[64ch]">
            <h2 className="text-2xl font-extrabold tracking-tight sm:text-3xl">Was wir selbst bauen</h2>
            <p className="mt-5 text-base leading-relaxed text-ink-muted">
              Kein Zusammenklicken fremder Bausteine und keine Weitergabe an Subunternehmer. App,
              Oberfläche, Backend, Datenmodell und KI-Anbindung entstehen bei uns. Die genannten
              Techniken sind die, mit denen unsere sechs Produkte tatsächlich laufen.
            </p>
          </div>

          <div className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {koennen.map((k) => (
              <article key={k.t} className="flex flex-col rounded-2xl border border-hairline bg-surface-raised p-7">
                <h3 className="text-[17px] font-bold tracking-tight">{k.t}</h3>
                <p className="mt-3 text-sm leading-relaxed text-ink-muted">{k.p}</p>
                <div className="mt-5 flex flex-wrap gap-1.5 border-t border-hairline pt-4">
                  {k.stack.map((tech) => (
                    <span key={tech} className="rounded-md bg-white/[0.06] px-2.5 py-1 text-[11px] font-medium text-ink-muted">
                      {tech}
                    </span>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ---- Betrieb: wir betreiben, was wir bauen ---- */}
      <section className="border-y border-hairline bg-surface-sunken py-24">
        <div className="container mx-auto grid gap-14 px-4 lg:grid-cols-[1.05fr_0.95fr]">
          <div>
            <h2 className="max-w-[16ch] text-2xl font-extrabold tracking-tight sm:text-3xl">
              Wir betreiben, was wir bauen
            </h2>
            <div className="mt-6 space-y-4 text-base leading-relaxed text-ink-muted">
              <p className="max-w-[56ch]">
                Jedes unserer Produkte läuft auf Infrastruktur, die wir selbst verantworten: eigene
                Server in deutschen Rechenzentren, eigene Auslieferung, eigene Überwachung. Kein
                Zwischenhändler, bei dem im Störungsfall die Spur endet.
              </p>
              <p className="max-w-[56ch]">
                Das ist keine Ideologie, sondern eine Voraussetzung. Souveränität und
                DSGVO-Konformität lassen sich nicht nachträglich aufsetzen, sie sind eine
                Architekturentscheidung.
              </p>
              <p className="max-w-[56ch]">
                Und es bedeutet, dass wir Störungen selbst sehen und selbst beheben, statt sie zu
                melden und zu warten.
              </p>
            </div>
          </div>

          <dl className="border-t border-hairline">
            {gelernt.map((g) => (
              <div key={g.t} className="border-b border-hairline py-5">
                <dt className="text-[15px] font-bold tracking-tight">{g.t}</dt>
                <dd className="mt-2 max-w-[54ch] text-sm leading-relaxed text-ink-muted">{g.p}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* Granalis als Leitprodukt */}
      <section className="border-y border-hairline bg-surface-sunken py-24">
        <div className="container mx-auto grid items-center gap-14 px-4 lg:grid-cols-2">
          <div>
            <span className="inline-flex items-center rounded-full border border-brand-400/25 bg-brand/10 px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-brand-300">
              Unser größtes Produkt
            </span>
            <h2 className="mt-5 text-3xl font-extrabold tracking-tight sm:text-4xl">Granalis</h2>
            <p className="mt-5 max-w-[56ch] text-base leading-relaxed text-ink-muted">
              Aus großen Beständen unstrukturierter Daten wird ein strukturierter, belegter und
              vergleichbarer Datenbestand. Granalis liest, was bisher nur Menschen lesen konnten,
              und macht daraus geprüftes Wissen.
            </p>
            <div className="mt-6 flex flex-wrap gap-2.5">
              {["Enterprise Knowledge Platform", "SaaS", "API-first"].map((tag) => (
                <span
                  key={tag}
                  className="rounded-full border border-hairline-strong px-3.5 py-1.5 text-xs text-ink-muted"
                >
                  {tag}
                </span>
              ))}
            </div>
            <dl className="mt-7 border-t border-hairline">
              {[
                "Jede Aussage weiss, aus welchem Dokument, welcher Seite und welcher Textstelle sie stammt.",
                "KI prüft KI. Kein ungeprüfter Wert landet im Bestand.",
                "Was unsicher bleibt, entscheidet ein Mensch. Nicht pauschal, sondern risikoabhängig.",
              ].map((text, i) => (
                <div key={i} className="flex gap-4 border-b border-hairline py-4 text-sm text-ink-muted">
                  <dt className="flex-none font-bold tabular-nums text-brand-300">
                    {String(i + 1).padStart(2, "0")}
                  </dt>
                  <dd>{text}</dd>
                </div>
              ))}
            </dl>
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-4">
              <Link
                to="/granalis"
                className="inline-flex h-11 items-center gap-2 rounded-lg bg-brand px-6 text-sm font-bold text-ink-onBrand transition-colors duration-200 hover:bg-brand-600"
              >
                Wie Granalis arbeitet
                <ArrowRight className="h-4 w-4" />
              </Link>
              <a
                href="https://granalis.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-300 transition-colors hover:text-brand-400"
              >
                granalis.com
                <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </div>

          <div className="overflow-hidden rounded-2xl border border-hairline bg-surface-raised">
            <img
              src="/img/produkte/granalis-hero.jpg"
              alt="Startseite von granalis.com: Aus unstrukturierten Daten wird ein Datenbestand, mit dem Sie arbeiten können"
              className="w-full"
              loading="lazy"
              width="1200"
              height="750"
            />
          </div>
        </div>
      </section>

      {/* Die übrigen fünf */}
      <section id="portfolio" className="py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-[62ch]">
            <h2 className="text-2xl font-extrabold tracking-tight sm:text-3xl">
              Fünf weitere Produkte im Markt
            </h2>
            <p className="mt-5 text-base leading-relaxed text-ink-muted">
              Jedes ist aus einem konkreten Problem entstanden, nicht aus einer Technologie. Und
              jedes läuft heute in der Hand echter Nutzer.
            </p>
          </div>

          <div className="mt-12 grid gap-5 md:grid-cols-2">
            {produkte.map((p) => (
              <a
                key={p.name}
                href={p.href}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex flex-col overflow-hidden rounded-2xl border border-hairline bg-surface-raised transition-colors duration-300 hover:border-hairline-strong"
              >
                <div className="overflow-hidden border-b border-hairline">
                  <img
                    src={p.bild}
                    alt={`Startseite von ${p.domain}`}
                    className="aspect-[16/9] w-full object-cover object-top transition-transform duration-700 ease-brand group-hover:scale-[1.025]"
                    loading="lazy"
                    width="1200"
                    height="675"
                  />
                </div>
                <div className="flex flex-1 flex-col gap-3 p-7">
                  <div className="flex items-baseline justify-between gap-4">
                    <h3 className="text-lg font-bold tracking-tight">{p.name}</h3>
                    <span className="text-xs text-ink-faint">{p.domain}</span>
                  </div>
                  <p className="text-sm leading-relaxed text-ink-muted">{p.text}</p>
                  <div className="mt-auto flex items-center justify-between gap-4 border-t border-hairline pt-4">
                    <span className="text-xs text-ink-faint">
                      <b className="font-semibold text-brand-300">{p.stand}</b> · {p.detail}
                    </span>
                    <ArrowUpRight className="h-4 w-4 flex-none text-ink-faint transition-all duration-300 group-hover:translate-x-0.5 group-hover:text-brand-300" />
                  </div>
                </div>
              </a>
            ))}

            <div className="flex items-center justify-center rounded-2xl border border-hairline bg-surface-raised p-10 text-center">
              <div>
                <h3 className="text-lg font-bold tracking-tight">Was daraus für Sie folgt</h3>
                <p className="mx-auto mt-3 max-w-[34ch] text-sm leading-relaxed text-ink-muted">
                  Wir kennen die Strecke von der Idee bis in den Betrieb aus eigener Erfahrung,
                  inklusive der Stellen, an denen es üblicherweise schiefgeht.
                </p>
                <Link
                  to="/contact"
                  className="mt-6 inline-flex h-11 items-center gap-2 rounded-lg border border-hairline-strong px-6 text-sm font-bold transition-colors duration-200 hover:bg-white/[0.06]"
                >
                  Gespräch vereinbaren
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Abschluss */}
      <section className="relative overflow-hidden border-t border-hairline bg-surface-sunken py-24 text-center">
        <div className="relative container mx-auto px-4">
          <h2 className="mx-auto max-w-[22ch] text-2xl font-extrabold tracking-tight sm:text-3xl">
            Dieselbe Methodik, die wir auf unsere Produkte anwenden, wenden wir auf Ihre an.
          </h2>
          <p className="mx-auto mt-5 max-w-[58ch] text-base leading-relaxed text-ink-muted">
            Sieben Fragen, rund zehn Minuten. Sie erhalten eine erste Einordnung Ihres Reifegrads
            und der größten Werthebel.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-4">
            <Link
              to="/ki-value-check"
              className="inline-flex h-11 items-center gap-2 rounded-lg bg-brand px-6 text-sm font-bold text-ink-onBrand shadow-[0_10px_30px_-12px_rgba(20,184,166,.8)] transition-colors duration-200 hover:bg-brand-600"
            >
              Value-Check starten
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 text-sm font-semibold text-brand-300 transition-colors hover:text-brand-400"
            >
              Gespräch vereinbaren
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Produkte;
